import { computed, Injectable, OnDestroy, Signal, inject } from '@angular/core';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { getVatType } from '../../../../shared/validators/custom-validators';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { CustomPortalError } from '../../../../core/errors/models/CustomPortalError';
import { SubscribeResponseDto } from '../models/PaymentDTO';
import { environment } from '../../../../../environments/environment';
import { StripeService } from '../../../../core/services/stripe.service';
import { PaymentService } from './payment.service';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { Store } from '@ngxs/store';
import { UserService } from '../../../../core/store/current-user/services/user.service';
import { Router } from '@angular/router';
import { CurrentUserState } from '../../../../core/store/current-user/current-user.state';
import { LocalStorageService } from '../../../../core/services/local-storage.services';
import { DataLayerService } from '../../../../core/services/analytics/data-layer.service';
import {
  PaymentData,
  UpgradeFreeTrialPaymentData,
} from '../models/PaymentData';
import { ProductDataService } from '../../product/services/product-data.service';
import { PaymentDataService } from './payment-data.service';
import { StripeUiService } from './StripeUiService';
import { Subscription } from '../../../../core/store/current-user/models/CurrentUserUIModel';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService implements OnDestroy {
  private readonly stripeService = inject(StripeService);
  private readonly stripeUiService = inject(StripeUiService);
  private readonly paymentService = inject(PaymentService);
  private readonly paymentDataService = inject(PaymentDataService);
  private readonly productDataService = inject(ProductDataService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  private readonly destroy$: Subject<void> = new Subject();
  subscriptionId: Signal<string | undefined>;
  subscription: Signal<Subscription | null | undefined>;

  stripe: Stripe | null = null;
  isLoading = false;

  readonly paymentData = computed<
    Omit<UpgradeFreeTrialPaymentData, 'formData' | 'paymentType'>
  >(() => ({
    quantity: this.productDataService.quantity() ?? 0,
    promotionCode: this.productDataService.promotionCode(),
    priceId: this.productDataService.priceId() ?? '',
    taxId: this.paymentDataService.taxId.value ?? '',
    companyName: this.paymentDataService.companyName.value ?? '',
    taxType: getVatType(this.paymentDataService.selectedCountry()),
  }));

  constructor() {
    this.subscriptionId = toSignal(
      this.store.select(CurrentUserState.subscriptionId),
    );
    this.subscription = toSignal(
      this.store.select(CurrentUserState.subscription),
    );
  }

  async init() {
    this.stripe = await this.stripeService.getStripe();
  }

  addFreeTrialPayment(
    addressFormData: UpgradeFreeTrialPaymentData['formData'],
    paymentType: string,
  ): Observable<SubscribeResponseDto> {
    const paymentData: UpgradeFreeTrialPaymentData = {
      ...this.paymentData(),
      formData: addressFormData,
      paymentType,
    };
    return this.paymentService.addFreeTrialPayment(
      paymentData,
      this.subscriptionId(),
    );
  }

  reactivateCanceledSubscription(
    addressFormData: UpgradeFreeTrialPaymentData['formData'],
    paymentType: string,
  ): Observable<SubscribeResponseDto> {
    const paymentData: UpgradeFreeTrialPaymentData = {
      ...this.paymentData(),
      formData: addressFormData,
      paymentType,
    };
    return this.paymentService.reactivateCanceledSubscription(
      paymentData,
      this.subscriptionId(),
    );
  }

  completePayment(
    addressFormData: PaymentData['formData'],
    paymentType: string,
  ): Observable<SubscribeResponseDto> {
    const paymentData: PaymentData = {
      ...this.paymentData(),
      purchaseIntentId: this.productDataService.purchaseIntentId(),
      formData: addressFormData,
      paymentType,
    };
    return this.paymentService.completePayment(paymentData);
  }

  async confirmPayment(elements: StripeElements | null, clientSecret: string) {
    if (!this.stripe || !elements) {
      return;
    }
    const result = await this.stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return_url:
          environment.general.baseUrl + '/portal/dashboard?checkout=success',
      },
      redirect: 'if_required',
    });

    if (result.error) {
      if (result.error.message) {
        this.snackbarService.errorMessage(result.error.message);
      }
    } else {
      if (await this.pollUserDataUntilConditionMet()) {
        this.onCheckoutSuccessful();
      }
    }
    this.isLoading = false;
  }

  async handleSubmitInvoice() {
    this.isLoading = true;
    const address =
      await this.stripeUiService.addressElementInvoice?.getValue();
    const addressFormData = address?.value;
    if (!addressFormData) {
      this.isLoading = false;
      return;
    }
    if (this.router.url === '/addPaymentMethod') {
      this.addFreeTrialPayment(
        addressFormData,
        this.paymentDataService.selectedMethod(),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async () => {
            if (await this.pollUserDataUntilConditionMet()) {
              this.onCheckoutSuccessful();
            }
            this.isLoading = false;
          },
          error: (err: CustomPortalError) => {
            this.snackbarService.error(err.errorType);
            this.isLoading = false;
          },
        });
    } else if (this.router.url === '/reactivate') {
      this.reactivateCanceledSubscription(
        addressFormData,
        this.paymentDataService.selectedMethod(),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async () => {
            if (await this.pollUserDataUntilConditionMet()) {
              this.onCheckoutSuccessful();
            }
            this.isLoading = false;
          },
          error: (err: CustomPortalError) => {
            this.snackbarService.error(err.errorType);
            this.isLoading = false;
          },
        });
    } else {
      this.completePayment(
        addressFormData,
        this.paymentDataService.selectedMethod(),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.onCheckoutSuccessful();
            this.isLoading = false;
          },
          error: (err: CustomPortalError) => {
            this.snackbarService.error(err.errorType);
            this.isLoading = false;
          },
        });
    }
  }

  async handleSubmitOnline() {
    this.isLoading = true;
    if (!this.stripeUiService.onlinePaymentElements) {
      return;
    }

    const { error: submitError } =
      await this.stripeUiService.onlinePaymentElements.submit();

    if (submitError) {
      // TODO: Show error toast?
      this.isLoading = false;
      return;
    }

    // Create the subscription
    const address = await this.stripeUiService.addressElement?.getValue();
    const addressFormData = address?.value;
    if (!addressFormData) {
      this.isLoading = false;
      return;
    }
    if (this.router.url === '/addPaymentMethod') {
      this.addFreeTrialPayment(
        addressFormData,
        this.paymentDataService.selectedOnlinePaymentType(),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async ({ clientSecret }: SubscribeResponseDto) => {
            await this.confirmPayment(
              this.stripeUiService.onlinePaymentElements,
              clientSecret,
            );
          },
          error: (error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
            this.isLoading = false;
          },
        });
    } else if (this.router.url === '/reactivate') {
      this.reactivateCanceledSubscription(
        addressFormData,
        this.paymentDataService.selectedOnlinePaymentType(),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async ({ type, clientSecret }: SubscribeResponseDto) => {
            if (type === 'setup') {
              return;
            }
            await this.confirmPayment(
              this.stripeUiService.onlinePaymentElements,
              clientSecret,
            );
          },
          error: (error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
            this.isLoading = false;
          },
        });
    } else {
      this.completePayment(
        addressFormData,
        this.paymentDataService.selectedOnlinePaymentType(),
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async ({ type, clientSecret }: SubscribeResponseDto) => {
            if (!this.stripe || !this.stripeUiService.onlinePaymentElements) {
              return;
            }

            const confirmIntent =
              type === 'setup'
                ? this.stripe.confirmSetup
                : this.stripe.confirmPayment;
            const result = await confirmIntent({
              elements: this.stripeUiService.onlinePaymentElements,
              clientSecret,
              confirmParams: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                return_url:
                  environment.general.baseUrl +
                  '/portal/dashboard?checkout=success',
              },
              redirect: 'if_required',
            });

            if (result.error) {
              if (result.error.message) {
                this.snackbarService.errorMessage(result.error.message);
              }
            } else {
              this.onCheckoutSuccessful();
            }
            this.isLoading = false;
          },
          error: (error: CustomPortalError) => {
            this.snackbarService.error(error.errorType);
            this.isLoading = false;
          },
        });
    }
  }

  async pollUserDataUntilConditionMet(
    timeLimitInSeconds = 20,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const now = new Date().getTime();
      let isFreeTrialing = true;
      const intervalId = setInterval(() => {
        this.userService.getUserData().subscribe({
          next: (userData) => {
            isFreeTrialing =
              userData.user.organization.subscriptions[0].freeTrialing;
          },
        });

        if (!isFreeTrialing) {
          clearInterval(intervalId);
          resolve(true);
        }

        if (new Date().getTime() - now >= timeLimitInSeconds * 1000) {
          this.snackbarService.error('ADD_PAYMENT_METHOD_FAILED');
          clearInterval(intervalId);
          resolve(false);
        }
      }, 500);
    });
  }

  onCheckoutSuccessful() {
    this.localStorageService.removeItem('user-email');
    this.sendCheckoutCompleteEventToDataLayer();
    void this.router.navigate(['/portal/dashboard'], {
      queryParams: { checkout: 'success' },
    });
  }

  private sendCheckoutCompleteEventToDataLayer() {
    if (!this.subscription()) {
      return;
    }
    this.dataLayerService.pushEventToDataLayer({
      event: 'checkout_complete',
      ecommerce: this.getEcommerceData(),
    });
  }

  async validatePurchaseIntent(purchaseIntentId: string): Promise<void> {
    await firstValueFrom(
      this.paymentService.validatePurchaseIntent(purchaseIntentId),
    );
  }

  getEcommerceData() {
    return {
      /* eslint-disable @typescript-eslint/naming-convention*/
      transaction_id: this.productDataService.purchaseIntentId(),
      value: this.productDataService.total(),
      items: [
        {
          item_id: this.productDataService.priceId(),
          quantity: this.productDataService.quantity() ?? undefined,
        },
        /* eslint-enable @typescript-eslint/naming-convention */
      ],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
